import { useInjection } from 'inversify-react';
import { memo, useCallback, useEffect } from 'react';

import {
  CanvasWrapper,
  CenteredFlexContainer,
  FullViewPort,
  StyledViewer,
} from '../app/app.styles';
import { TYPES } from '../inversify.types';
import { IViewerStoreInjector } from '../store';
import { Status } from '../store/viewer/viewer.types';
import { calculateZoomForFitToView } from '../utils/zoom.util';
import { StyledLayoutWrapper } from './Layout.styles';
import { AnimatedNavBar } from './components/AnimatedNavBar/AnimatedNavBar';
import { LayoutSkeleton } from './components/Skeleton/LayoutSkeleton';
import { ErrorPage } from './pages';
import { Login } from './pages/Login';

export interface LayoutProps {
  children: React.ReactNode;
}

export const Layout = memo(({ children }: LayoutProps) => {
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const status = useViewerStore((state) => state.status);
  const setViewOptions = useViewerStore((state) => state.setViewOptions);
  const setZoom = useViewerStore((state) => state.setZoom);
  const pageDimension = useViewerStore((state) => state.pageDimension);

  const initializeFitToViewOnMount = useCallback(() => {
    const fitToViewPercentage = calculateZoomForFitToView(pageDimension);

    setZoom(fitToViewPercentage);
    setViewOptions('fit');
  }, [pageDimension, setViewOptions, setZoom]);

  useEffect(initializeFitToViewOnMount, [initializeFitToViewOnMount]);

  if (status === Status.Loading) {
    return <LayoutSkeleton />;
  }

  if (status === Status.CredentialsInvalid) {
    return <Login />;
  }

  if (
    status === Status.ProjectNotFound ||
    status === Status.ProjectLocked ||
    status === Status.UnauthorizedUser
  ) {
    return <ErrorPage status={status} />;
  }

  if (status === Status.UnknownError) {
    return <ErrorPage />;
  }

  return (
    <AnimatedNavBar>
      {({ documentClickHandler }) => (
        <StyledLayoutWrapper data-testid="layout-wrapper" onClick={documentClickHandler}>
          <StyledViewer>
            <FullViewPort>
              <CanvasWrapper>
                <CenteredFlexContainer>{children}</CenteredFlexContainer>
              </CanvasWrapper>
            </FullViewPort>
          </StyledViewer>
        </StyledLayoutWrapper>
      )}
    </AnimatedNavBar>
  );
});
